import React, { useEffect, useCallback } from 'react';
import moment from 'moment';
import Router from 'next/router';
import { useSelector, useDispatch } from 'react-redux';

import { showModal } from 'actions/modal';

import Text from 'components/core/Text';
import StartRestartResumeButton from 'components/StartRestartResumeButton';
import ProgramSchedule from 'components/ProgramSchedule';
import Activities from 'containers/Pages/Program/Activities';
import imageUrlGenerator from 'utils/imageUrlGenerator';
import dateOfNextSession from 'utils/dateOfNextSession';
import { sanitizeRichTextHtml } from 'utils/sanitizeHtml';
import SessionNavigation from 'components/Pages/SessionDetails/SessionNavigation';
import { getProgramById } from 'reducers/programs';
import SessionBreakdown from './components/SessionBreakdown';
import Container from '../Container';

import {
  StyledBack,
  Wrapper,
  Section,
  SectionHeader,
  Program,
  ProgramImage,
  Details,
  Tracks,
  TrackOption,
  Pill,
  TrackHowTo,
  Summary,
  RightColumn,
  RightSection,
  SectionTitle,
  InfoGroup,
  InfoLabel,
  StatValue,
  RestartButton,
  StyledInternalLink,
  Information,
  InformationTitle,
  TitleWrapper,
} from './styles';

const DATE_FORMAT = 'ddd, MMM D';

const renderSlicedProgramName = (name) => {
  return name;
};

const renderCurrentDay = (program) => {
  const { completedSessionsCount, nextSession, sessionsCount, programTrack } = program;
  const isLooping = programTrack?.loopBackToTrackSessionId != null;

  if (isLooping) {
    return nextSession?.name ?? '';
  }

  if (completedSessionsCount > 0 && completedSessionsCount % sessionsCount === 0) {
    return 'completed';
  }

  if (completedSessionsCount < sessionsCount && nextSession) {
    return `${nextSession?.name}`;
  }

  return '';
};

const Purchased = ({ programId, clearExercisesState }) => {
  const dispatch = useDispatch();
  const userProgram = useSelector((state) => getProgramById(state, Number(programId)));
  const {
    schedule,
    sessionsCount,
    externalLink,
    nextSession,
    sessions,
    programTrack,
    programTracks,
    nextTrackSessionId,
  } = userProgram || {};
  const programTrackSessions = programTrack?.programTrackSessions;
  const hasTrackHowTo = programTracks?.some((track) => track.howTo);
  const filteredSchedule = schedule ? Object.keys(schedule).filter((key) => schedule[key]) : [];
  const nextSessionDate = userProgram?.lastSession?.workout
    ? dateOfNextSession(filteredSchedule, userProgram.lastSession.workout.startedAt, DATE_FORMAT)
    : null;

  useEffect(() => {
    if (externalLink) {
      Router.push(externalLink);
    }
  }, [externalLink]);

  const ptsIdx = programTrackSessions?.findIndex((pts) => pts.id === nextTrackSessionId);
  const isLoopingProgram = programTrack?.loopBackToTrackSessionId != null;
  const sessionDaysCompleted = (ptsIdx / (sessionsCount - 1)) * 100;

  const handleTrackClick = useCallback(
    (track) => {
      if (track.id === userProgram?.programTrack?.id) return;
      dispatch(
        showModal('CHANGE_TRACK', {
          programId: Number(programId),
          trackId: track.id,
          trackName: track.internalName,
        }),
      );
    },
    [dispatch, programId, userProgram?.programTrack?.id],
  );

  if (!userProgram) return null;

  return (
    <Wrapper>
      <Section first>
        <Container>
          <StyledBack theme="white" themeMobile="white" onClick={() => Router.push('/programs')} />
          <Program
            sessionsDaysProgress={sessionDaysCompleted}
            isLooping={isLoopingProgram}
            key={userProgram.id}
            purchased
            inprogress={!!userProgram?.workoutsCount}
          >
            <ProgramImage src={imageUrlGenerator(userProgram.picture, '930x523', '400x250')} />
            <Information>
              <Text variant="captionSmall" color="white" withShadow>
                {renderCurrentDay(userProgram)}
              </Text>
              <InformationTitle>
                <Text variant="mainHeader" tag="h3" color="white" isCondensed withShadow>
                  {renderSlicedProgramName(userProgram.name)}
                </Text>
              </InformationTitle>
              <StartRestartResumeButton
                program={userProgram}
                clearExercisesState={clearExercisesState}
              />
            </Information>
          </Program>
          <Details>
            <Summary>
              <Text variant="sectionHeader" transform="uppercase" mb={4}>
                PROGRAM SUMMARY
              </Text>
              <Text>
                {}
                <span
                  dangerouslySetInnerHTML={{
                    __html: sanitizeRichTextHtml(userProgram.description),
                  }}
                />
              </Text>
            </Summary>
            <RightColumn>
              <InfoGroup>
                {!isLoopingProgram && (
                  <div>
                    <StatValue>{programTrack?.sessionsCount ?? sessionsCount}</StatValue>
                    <InfoLabel dark>SESSIONS</InfoLabel>
                  </div>
                )}
                <div>
                  <StatValue>
                    {Object.keys(sessions[0]?.durationOptions ?? {})
                      .filter((duration) => sessions[0]?.durationOptions[duration])
                      .join('·')}
                  </StatValue>
                  <InfoLabel dark>MINUTE OPTIONS</InfoLabel>
                </div>
                {userProgram.lastSession?.workout ? (
                  <>
                    <div>
                      <Text variant="sectionHeader">
                        {moment(userProgram.lastSession.workout.startedAt).format(DATE_FORMAT)}
                      </Text>
                      <InfoLabel>LAST SESSION</InfoLabel>
                    </div>
                    {nextSessionDate ? (
                      <div>
                        <Text variant="sectionHeader">
                          {nextSessionDate === moment().format(DATE_FORMAT)
                            ? 'Today'
                            : nextSessionDate}
                        </Text>
                        <InfoLabel>NEXT SESSION</InfoLabel>
                      </div>
                    ) : null}
                  </>
                ) : null}
              </InfoGroup>
              {programTracks?.length > 1 ? (
                <RightSection>
                  <SectionTitle>TRACK</SectionTitle>
                  <Tracks stacked={hasTrackHowTo}>
                    {programTracks.map((track) => (
                      <TrackOption key={track.id}>
                        <Pill
                          type="button"
                          selected={programTrack.id === track.id}
                          aria-pressed={programTrack.id === track.id}
                          onClick={() => handleTrackClick(track)}
                        >
                          {track.internalName}
                        </Pill>
                        {hasTrackHowTo && (
                          <Text variant="textSmall" tag="div" color="gray">
                            <TrackHowTo
                              dangerouslySetInnerHTML={{
                                __html: sanitizeRichTextHtml(track.howTo),
                              }}
                            />
                          </Text>
                        )}
                      </TrackOption>
                    ))}
                  </Tracks>
                </RightSection>
              ) : null}
              <RightSection>
                <ProgramSchedule program={userProgram} />
              </RightSection>
              {!isLoopingProgram && (
                <RestartButton
                  type="button"
                  onClick={() => Router.push(`/programs/${programId}/intro`)}
                >
                  Restart Program
                </RestartButton>
              )}
            </RightColumn>
          </Details>
        </Container>
      </Section>
      <Section less>
        <SessionNavigation
          nextSession={nextSession}
          program={userProgram}
          nextTrackSessionId={nextTrackSessionId}
        />
      </Section>
      <Section left>
        <SectionHeader>
          <TitleWrapper>
            <Text variant="sectionHeader" transform="uppercase">
              COMPLETED SESSIONS{' '}
            </Text>
            <StyledInternalLink href="/activity">See all</StyledInternalLink>
          </TitleWrapper>
        </SectionHeader>
        <Activities programId={programId} />
      </Section>
      <Section right>
        <SectionHeader>
          <Text variant="sectionHeader" transform="uppercase">
            UPCOMING SESSIONS
          </Text>
        </SectionHeader>
        <SessionBreakdown program={userProgram} />
      </Section>
    </Wrapper>
  );
};

export default Purchased;
